import React, { useState, useEffect, useRef } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import {
  propertyApi,
  type PropertyEvaluation,
  type PropertyData,
} from '../services';
import './PropertyEvaluationChart.css';

type TimePeriod = '1month' | '6months' | '12months' | '5years' | 'all';

interface PropertyEvaluationChartProps {
  property: PropertyData;
  refreshTrigger?: number; // Increment this to trigger a refresh
  timePeriod?: TimePeriod;
}

const PropertyEvaluationChart: React.FC<PropertyEvaluationChartProps> = ({
  property,
  refreshTrigger,
  timePeriod = '12months',
}) => {
  const [evaluations, setEvaluations] = useState<PropertyEvaluation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [chartWidth, setChartWidth] = useState<number>(600);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // Update chart width based on container size
  useEffect(() => {
    const updateWidth = () => {
      if (chartWrapperRef.current) {
        const width = chartWrapperRef.current.offsetWidth;
        // Use the full width minus a small margin for better fit
        setChartWidth(Math.max(400, width - 20));
      }
    };

    // Initial width calculation
    updateWidth();

    // Update on window resize
    window.addEventListener('resize', updateWidth);

    // Also update after a short delay to ensure container is rendered
    const timeoutId = setTimeout(updateWidth, 100);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timeoutId);
    };
  }, [evaluations.length]); // Recalculate when data changes

  useEffect(() => {
    const fetchEvaluations = async () => {
      if (!property.id || property.id === 'new') {
        return;
      }

      setLoading(true);
      try {
        const data = await propertyApi.getEvaluations(property.id, timePeriod);
        setEvaluations(data);
      } catch (error) {
        console.error('Failed to fetch evaluations:', error);
        setEvaluations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, [property.id, timePeriod, refreshTrigger]);

  // Prepare chart data from evaluations
  const evalData = evaluations.map((evaluation) => {
    const evalDate = new Date(evaluation.timestamp);
    return {
      timestamp: evalDate.toLocaleDateString(),
      date: evalDate,
      vw: evaluation.vw ? Number(evaluation.vw) : null,
      vw_lwr: evaluation.vwLwr ? Number(evaluation.vwLwr) : null,
      vw_upr: evaluation.vwUpr ? Number(evaluation.vwUpr) : null,
    };
  });

  // Sort evaluations by date
  evalData.sort((a, b) => a.date.getTime() - b.date.getTime());

  // Determine kaufDate
  const kaufDate = property.kaufdatum ? new Date(property.kaufdatum) : null;
  const oldestValuationDate = evalData.length > 0 ? evalData[0].date : null;

  // Final chartData array (we will add kaufDate to chartData if it fits in zoom range)
  let chartData = [...evalData];

  // If kaufDate is newer than or equal to the oldest valuation, inject a placeholder node
  if (
    kaufDate &&
    oldestValuationDate &&
    kaufDate.getTime() >= oldestValuationDate.getTime()
  ) {
    const alreadyExists = evalData.some(
      (d) => d.date.toDateString() === kaufDate.toDateString(),
    );
    if (!alreadyExists) {
      chartData.push({
        timestamp: kaufDate.toLocaleDateString(),
        date: kaufDate,
        vw: null,
        vw_lwr: null,
        vw_upr: null,
      });
      chartData.sort((a, b) => a.date.getTime() - b.date.getTime());
    }
  }

  // Calculate min and max for Y-axis
  const allValues = [
    ...evaluations
      .map((e) => e.vw)
      .filter((v) => v !== null && v !== undefined),
    ...evaluations
      .map((e) => e.vwLwr)
      .filter((v) => v !== null && v !== undefined),
    ...evaluations
      .map((e) => e.vwUpr)
      .filter((v) => v !== null && v !== undefined),
    property.kaufpreis || 0,
  ].filter((v) => v !== null && v !== undefined) as number[];

  const minValue = allValues.length > 0 ? Math.min(...allValues) * 0.9 : 0;
  const maxValue = allValues.length > 0 ? Math.max(...allValues) * 1.1 : 100000;

  const formatCurrency = (value: number) => {
    // For values >= 1 million, format as "€ X,X Mio"
    if (value >= 1000000) {
      const millions = value / 1000000;
      // Format to one decimal place with a comma separator (German locale)
      return `€ ${millions.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Mio`;
    }
    // For values < 1 million, format with thousand separators (German locale)
    // e.g., 500000 becomes "500.000"
    return `€ ${value.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`;
  };

  // Prepare data for @mui/x-charts
  const xAxisData = chartData.map((d) => d.date);

  // Prepare horizontal line data for buy price (constant value starting from kaufdatum)
  const buyPriceHorizontalLine = property.kaufpreis
    ? chartData.map((d) => {
        if (kaufDate) {
          const dMidnight = new Date(d.date);
          dMidnight.setHours(0, 0, 0, 0);
          const kMidnight = new Date(kaufDate);
          kMidnight.setHours(0, 0, 0, 0);
          if (dMidnight.getTime() < kMidnight.getTime()) {
            return null;
          }
        }
        return property.kaufpreis!;
      })
    : [];

  if (loading) {
    return (
      <div className="evaluation-chart-container">
        <div className="chart-loading">Loading chart data...</div>
      </div>
    );
  }

  if (evaluations.length === 0) {
    return (
      <div className="evaluation-chart-container">
        <div className="chart-empty">
          No evaluation data available. Click "Evaluate Now" to generate
          evaluations.
        </div>
      </div>
    );
  }

  return (
    <div className="evaluation-chart-container">
      <div className="chart-wrapper" ref={chartWrapperRef}>
        <LineChart
          width={chartWidth}
          height={400}
          series={[
            // Series for the lower bound line
            {
              id: 'lower_bound_line',
              data: chartData.map((e) => e.vw_lwr),
              label: 'Estimated Lower Bound',
              color: '#8ce99a', // Lighter green
              curve: 'monotoneX',
              showMark: false,
              connectNulls: true,
            },
            // Series for the upper bound line
            {
              id: 'upper_bound_line',
              data: chartData.map((e) => e.vw_upr),
              label: 'Estimated Upper Bound',
              color: '#8ce99a', // Lighter green
              curve: 'monotoneX',
              showMark: false,
              connectNulls: true,
            },
            // Series for the average valuation line
            {
              data: chartData.map((e) => e.vw),
              label: 'Valuation',
              color: '#4FFF84', // Green from ref
              showMark: true,
              curve: 'monotoneX',
              connectNulls: true,
            },
            // Series for the buy price horizontal line
            {
              id: 'buyPriceHorizontalLine',
              data: buyPriceHorizontalLine,
              label: 'Buy Price',
              color: '#A7A3E2', // Soft purple from ref
              showMark: false,
              curve: 'linear',
              connectNulls: true,
            },
          ]}
          xAxis={[
            {
              data: xAxisData,
              scaleType: 'time',
              valueFormatter: (date: Date | string) => {
                const d = date instanceof Date ? date : new Date(date);
                return d.toLocaleString('default', {
                  month: 'short',
                  year: 'numeric',
                });
              },
              tickNumber: 4,
            },
          ]}
          yAxis={[
            {
              valueFormatter: (value: number) => formatCurrency(value),
              min: minValue,
              max: maxValue,
              width: 100,
            },
          ]}
          sx={{
            '& .MuiChartsAxis-directionX .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel':
              {
                textAnchor: 'middle',
                fill: '#A7A3E2',
              },
            '& .MuiChartsAxis-directionY .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel':
              {
                fill: '#A7A3E2',
              },
            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
              stroke: '#A7A3E2',
            },
            '& .MuiLineElement-root[data-series="upper_bound_line"]': {
              strokeDasharray: '5 5',
              strokeWidth: 1,
              opacity: 0.6,
            },
            '& .MuiLineElement-root[data-series="lower_bound_line"]': {
              strokeDasharray: '5 5',
              strokeWidth: 1,
              opacity: 0.6,
            },
            '& .MuiLineElement-root[data-series="buyPriceHorizontalLine"]': {
              strokeDasharray: '3 3',
              strokeWidth: 2,
            },
            '& .MuiMarkElement-root': {
              fill: '#384B77',
              stroke: '#4FFF84',
              strokeWidth: 2,
            },
            '& .MuiChartsGrid-line': {
              stroke: '#A7A3E2',
              strokeOpacity: 0.2,
            },
          }}
          grid={{ vertical: true, horizontal: true }}
          margin={{ top: 20, right: 30, left: 55, bottom: 90 }}
          slots={{
            tooltip: (props: any) => {
              // Filter out the 'buyPricePoint' series from the tooltip
              if (props.axisData && props.axisData.series) {
                const filteredSeries = props.axisData.series.filter(
                  (s: any) => s.id !== 'buyPricePoint',
                );
                return (
                  <ChartsTooltip
                    {...props}
                    axisData={{
                      ...props.axisData,
                      series: filteredSeries,
                    }}
                  />
                );
              }
              return <ChartsTooltip {...props} />;
            },
          }}
          slotProps={{
            legend: {
              position: { vertical: 'bottom', horizontal: 'center' },
            },
            tooltip: {
              trigger: 'axis',
            },
          }}
        />
      </div>
    </div>
  );
};

export default PropertyEvaluationChart;
